// AI Generated Component
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { PagePolicyRepoService } from '@client/app/services/api/page-policy/page-policy-repo.service';
import { PolicyRepoService } from '@client/app/services/api/policy-repo/policy-repo.service';

@Component({
    selector: 'page-details-section-policies',
    standalone: false,
    template: `
<div id="page-details-policies">
    <div class="row page-details-section-header">
        <div class="col-48">
            <page-header [icon]="['MENUES', 'POLICIES'] | monIcons" [header]="'Content policies' | translate">
                <page-header-content>
                    <div class="row">
                        <div class="col-48 text-right">
                            <table-compliance
                                    class="inline-block pr-2"
                                    [data]="[domainPolicies - pagePolicyCount, pagePolicyCount]"
                                    [labels]="['', '']"
                                    size="46"
                                    [btnWord]="'Overall policy compliance for this page' | translate"
                                    [indexNumber]="0">
                            </table-compliance>
                        </div>
                    </div>
                </page-header-content>
            </page-header>
        </div>
    </div>

    <div class="row page-details-section-body align-items-stretch">
        <div class="col-lg-30 pr-lg-0">
            <div class="scroll max-height">
                <page-details-policies-table (selectPolicy)="selectPolicy($event.policy)"
                                             [policy]="policy"
                                             [policies]="policies"
                                             (getPolicies)="getPage()"
                                             (onTabChange)="onTabChange($event.tab)"
                                             (onFilterChange)="onFilterChange($event.changes)"
                                             [progress]="progress">
                </page-details-policies-table>
            </div>
        </div>
        <div class="col-lg-18 pl-lg-0">
            <div class="scroll max-height">
                <mon-spinner [progress]="progress">
                    <page-details-policy-information [page]="page"
                                                     [refreshPolicies]="refreshPolicies"
                                                     [policy]="policy"
                                                     [matchLimit]="matchLimit">
                    </page-details-policy-information>
                </mon-spinner>
            </div>
        </div>
    </div>
</div>
    `,
})
export class PageDetailsSectionPoliciesComponent implements OnInit, OnDestroy {
    @Input() page: any;
    @Input() active: any;
    @Output() refreshPage = new EventEmitter<any>();

    matchLimit = 100;
    policyMode: any = null;
    policyScheduled: any = null;
    policy: any = null;
    policies: any[] = [];
    progress: any;
    timerPromise: any = true;
    domainPolicies = 0;
    pagePolicyCount = 0;

    constructor (
        private pagePolicyRepoService: PagePolicyRepoService,
        private policyRepoService: PolicyRepoService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnDestroy (): void {
        this.destroyTimer();
    }

    activate (): void {
        this.policyMode = null;
        this.policyScheduled = null;
        this.policy = this.active || null;
        this.refreshPolicies();
        this.timerPromise = true;
    }

    refreshPolicies = (refreshPage?: boolean): void => {
        this.getPage();
        this.getPolicyCounts();
        if (typeof refreshPage === 'boolean' && refreshPage === true) {
            this.refreshPage.emit({ target: 'page-details-section-policies' });
        }
    };

    getPage = (): void => {
        this.progress = this.getCollectedPolicies();
    };

    getPolicyCounts (): void {
        const promises: Promise<any>[] = [];
        promises.push(this.getDomainPolicies(null, null));
        promises.push(this.getPagePolicies(null, null));

        Promise.all(promises).then((values) => {
            this.pagePolicyCount = values[1].filter((policy: any) => {
                return !policy.ignored;
            }).length;
            const count = values[0];
            this.domainPolicies = Array.isArray(count) ? count.length : 0;
        }, () => {});
    }

    getCollectedPolicies (): Promise<any> {
        const promises: Promise<any>[] = [];
        promises.push(this.getDomainPolicies());
        promises.push(this.getPagePolicies());
        return Promise.all(promises).then((values) => {
            this.policies = this.mergePageWithDomainPolicies(this.getUniquePolicies(values[1]), values[0]).sort((a: any, b: any) => {
                // Sort policies
                if (a.ignored !== b.ignored) {
                    return a.ignored === true ? 1 : -1;
                }
                if (a.state === 'running' || b.state === 'running') {
                    if (a.state === 'running' && b.state !== 'running') {
                        return -1;
                    }

                    if (a.state !== 'running' && b.state === 'running') {
                        return 1;
                    }

                    return 0;
                }
                return a.matches_count > b.matches_count ? -1 : b.matches_count > a.matches_count ? 1 : 0;
            });

            this.selectPolicy(this.policy);

            let autoReloadStarted = false;
            for (let i = 0; i < this.policies.length; i++) {
                if (this.policies[i].state === 'running' && !autoReloadStarted && this.timerPromise !== null) {
                    this.startAutoReload();
                    autoReloadStarted = true;
                }
            }
            return values;
        }, () => {});
    }

    getDomainPolicies (violation?: any, scheduled?: any): Promise<any> {
        const params = {
            page_size: 0,
            mode: violation !== undefined ? violation : this.policyMode,
            scheduled: scheduled !== undefined ? scheduled : this.policyScheduled,
        };
        return this.policyRepoService.domainGetAll(params).then((policies: any[]) => {
            return policies.map((policy: any) => {
                policy.matches_count = 0;
                return policy;
            });
        });
    }

    getPagePolicies (violation?: any, scheduled?: any): Promise<any> {
        const params = {
            page_size: 0,
            mode: violation !== undefined ? violation : this.policyMode,
            scheduled: scheduled !== undefined ? scheduled : this.policyScheduled,
        };

        return this.pagePolicyRepoService.getAll(this.page.id, params);
    }

    getUniquePolicies (policies: any[]): any[] {
        const policyIds: any[] = [];
        return policies.filter((policy: any) => {
            if (policyIds.indexOf(policy.id) === -1) {
                policyIds.push(policy.id);
                return true;
            }
            return false;
        });
    }

    mergePageWithDomainPolicies (pagePolicies: any[], domainPolicies: any[]): any[] {
        const policyIds = pagePolicies.map((policy: any) => {
            return policy.id;
        });
        return pagePolicies.concat(
            domainPolicies.filter((policy: any) => {
                return policyIds.indexOf(policy.id) === -1;
            }),
        );
    }

    selectPolicy = (policy: any): void => {
        this.resetMatchLimit();
        if (this.policies.length > 0) {
            if (policy) {
                const foundPolicy = this.policies.find((p: any) => {
                    return p.id === policy.id;
                });
                this.policy = foundPolicy ? foundPolicy : this.policies[0];
            } else {
                this.policy = this.policies[0];
            }
        }
    };

    onTabChange = (tab: string): void => {
        switch (tab) {
            case 'unwanted':
                this.policyMode = 'violation';
                break;
            case 'search':
                this.policyMode = 'search';
                break;
            case 'required':
                this.policyMode = 'required';
                break;
            default:
                this.policyMode = null;
                break;
        }
        this.getPage();
    };

    onFilterChange = (changes: any): void => {
        if (changes.scheduled && changes.scheduled.length > 0) {
            this.policyScheduled = changes.scheduled[changes.scheduled.length - 1].value;
        } else {
            this.policyScheduled = null;
        }
        this.getPage();
    };

    destroyTimer (): void {
        if (typeof this.timerPromise === 'object') {
            // In Angular, we would use clearTimeout instead of $timeout.cancel
            clearTimeout(this.timerPromise);
        }
        this.timerPromise = null;
    }

    startAutoReload (): void {
        this.timerPromise = setTimeout(() => {
            this.getCollectedPolicies();
        }, 30000);
    }

    resetMatchLimit (): void {
        this.matchLimit = 100;
    }
}
